import { getFragmentData } from '@client/graphql/types';
import {
  MenuProductConfiguration,
  MenuProductPriceMode,
  MenuSectionTimeRuleType,
  ProductConfigurationType,
  MenuSectionTimeRuleUnion,
} from '@client/graphql/types/graphql';
import { naturalCompare } from '@client/module/utils/natural-compare.ts';
import { ProductMenuBySection } from '@client/page/management/menu/menu-detail/logic/group-menu-product-item-by-section.ts';
import { useDeleteMenuProduct } from '@client/page/management/menu/menu-product-edit/logic/use-delete-menu-product';
import { useUpdateMenuProduct } from '@client/page/management/menu/menu-product-edit/logic/use-update-menu-product';
import { useDuplicateMenuProduct } from '@client/page/management/menu/menu-product-edit/logic/use-duplicate-menu-product';
import { useUpdateMenuSection } from '@client/page/management/menu/menu-section-edit/logic/use-update-menu-section';
import useDeleteMenuSection from '@client/page/management/menu/menu-section-edit/logic/use-delete-menu-section';
import { EyeIcon, EyeSlashIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { useCompanyCurrency } from '@packages/core/company/focused-company-context.ts';
import { formatCurrency } from '@packages/core/utils/currency-formatter.ts';
import { MenuProductConfigurationValueFragment } from '@packages/network-graphql/common/menu-product-configuration-value-fragment.ts';
import { useDialog } from '@packages/ui/modal/use-dialog.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import DotStatus from '@packages/ui/status/dot-status';
import TableHeaderColumn from '@packages/ui/table/table-header-column.tsx';
import TableRowColumn from '@packages/ui/table/table-row-cell.tsx';
import TableRow from '@packages/ui/table/table-row.tsx';
import { TableSection } from '@packages/ui/table/table-section.tsx';
import { TableCellAlignment } from '@packages/ui/table/table-type.ts';
import Table from '@packages/ui/table/table.tsx';
import { useTranslate } from '@tolgee/react';
import { useNavigate } from 'react-router-dom';

export default function MenuProductTable(props: { group: ProductMenuBySection[] }) {
  const sortedProductMenuBySections = props.group.sort((a, b) =>
    naturalCompare(a.section?.code ?? '', b.section?.code ?? ''),
  );
  const { t } = useTranslate();
  return (
    <div className="overflow-scroll">
      <Table
        data={sortedProductMenuBySections}
        build={(group) => <Group key={group.section?.id ?? 'no-section'} group={group} />}
      >
        <TableHeaderColumn className="w-5" />
        <TableHeaderColumn>{t('common.table-header-column.product')}</TableHeaderColumn>
        <TableHeaderColumn className="w-32 text-right">{t('common.table-header-column.actions')}</TableHeaderColumn>
      </Table>
    </div>
  );
}

function Group(props: { group: ProductMenuBySection }) {
  const navigate = useNavigate();
  const { group } = props;
  const currency = useCompanyCurrency();
  const { t } = useTranslate();
  const dialog = useDialog();
  const { showAlert } = useNotificationCenter();
  const [updateMenuProduct, { loading: updating }] = useUpdateMenuProduct();
  const [deleteMenuProduct, { loading: deleting }] = useDeleteMenuProduct();
  const [duplicateMenuProduct, { loading: duplicating }] = useDuplicateMenuProduct();
  const [updateMenuSection, { loading: updatingSection }] = useUpdateMenuSection();
  const [deleteMenuSection, { loading: deletingSection }] = useDeleteMenuSection();

  // Helper function để build input mutation cho configurations
  function processConfigurationData(
    configurations: MenuProductConfiguration[],
    priceMode: MenuProductPriceMode,
    title: string,
  ) {
    return configurations
      .map((config: MenuProductConfiguration) => {
        const { id: configId, title: configTitle, type, values } = config;
        return {
          id: configId,
          title: configTitle,
          type,
          values: values.map((value: MenuProductConfiguration['values'][number], valueIndex: number) => {
            const { id: valueId, name, price } = value;
            let processName = name;
            if (
              priceMode === MenuProductPriceMode.Simple &&
              type === ProductConfigurationType.Base &&
              valueIndex === 0 &&
              values.length === 1
            ) {
              processName = title;
            }
            return {
              id: valueId,
              name: processName,
              price: price,
            };
          }),
        };
      })
      .filter(({ type, values }) => !(type === ProductConfigurationType.Option && values.length === 0));
  }

  // Helper function để process time rule data
  function processTimeRuleData(timeRule: MenuSectionTimeRuleUnion | null | undefined) {
    if (!timeRule) {
      return {
        type: MenuSectionTimeRuleType.Always,
      };
    }

    if (timeRule.__typename === 'MenuSectionAlwaysTimeRule') {
      return {
        type: timeRule.type,
      };
    }

    if (timeRule.__typename === 'MenuSectionRangeTimeRule') {
      return {
        type: timeRule.type,
        start: timeRule.start,
        end: timeRule.end,
        daysOfWeek: timeRule.daysOfWeek ?? [],
        isAllDays: timeRule.isAllDays ?? false,
      };
    }

    return {
      type: MenuSectionTimeRuleType.Always,
    };
  }

  return (
    <>
      <TableSection key={group.section?.id} colSpan={3}>
        <div className="flex items-center justify-center gap-x-3 w-full py-2">
          {/* DotStatus column for section: empty for now, or you can add a status if needed */}
          <div className="flex flex-wrap items-center gap-1 font-semibold text-indigo-700">
            {/* <div className="w-4 md:w-11"></div> */}
            <span>
              {group.section?.code ? `${group.section.code} -` : t('menu.menu-detail.table-product.no-code', 'No code')}
              {group.section && !group.section.code && t('menu.menu-detail.table-product.no-position')}
              {!group.section && t('menu.menu-detail.table-product.emoty', 'Category empty')}
            </span>
            {group.section?.name && <span>{group.section.name}</span>}
            {group.section && !group.section.inStock && (
              <span className="border py-1 px-2 rounded-full border-red-600 bg-red-100 text-red-600 text-xs ml-2">
                {t('menu-product.card.out-of-stock', 'Out of stock')}
              </span>
            )}
          </div>
          {/* Right: Quick Actions */}
          <div className="flex items-center gap-2 justify-end">
            {group.section && (
              <PencilIcon
                tabIndex={0}
                aria-label="Edit"
                role="button"
                className="w-5 h-5 text-blue-500 cursor-pointer"
                onClick={() => {
                  navigate(`sections/${group.section?.id ?? ''}/edit`);
                }}
              />
            )}
            {/* Hide/Show on menu section */}
            {group.section &&
              (group.section.enabled ? (
                <EyeIcon
                  tabIndex={0}
                  aria-label="Visible"
                  role="button"
                  className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                    updatingSection ? 'pointer-events-none opacity-50' : ''
                  }`}
                  onClick={() => {
                    void (async () => {
                      if (!group.section) return;
                      await updateMenuSection({
                        variables: {
                          id: group.section.id,
                          input: {
                            code: group.section.code,
                            name: group.section.name,
                            description: group.section.description,
                            discountable: group.section.discountable,
                            inStock: group.section.inStock,
                            enabled: !group.section.enabled,
                          },
                          timeRuleInput: processTimeRuleData(group.section.timeRule),
                        },
                      });
                      showAlert({
                        status: 'success',
                        title: t('common.success'),
                        message: t('Cập nhật trạng thái thành công!'),
                      });
                    })();
                  }}
                />
              ) : (
                <EyeSlashIcon
                  tabIndex={0}
                  aria-label="Hidden"
                  role="button"
                  className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                    updatingSection ? 'pointer-events-none opacity-50' : ''
                  }`}
                  onClick={() => {
                    void (async () => {
                      if (!group.section) return;
                      await updateMenuSection({
                        variables: {
                          id: group.section.id,
                          input: {
                            code: group.section.code,
                            name: group.section.name,
                            description: group.section.description,
                            discountable: group.section.discountable,
                            inStock: group.section.inStock,
                            enabled: !group.section.enabled,
                          },
                          timeRuleInput: processTimeRuleData(group.section.timeRule),
                        },
                      });
                      showAlert({
                        status: 'success',
                        title: t('common.success'),
                        message: t('Cập nhật trạng thái thành công!'),
                      });
                    })();
                  }}
                />
              ))}
            {/* Delete section */}
            {group.section && (
              <TrashIcon
                tabIndex={0}
                aria-label="Delete"
                role="button"
                className={`w-5 h-5 text-red-500 cursor-pointer ${
                  deletingSection ? 'pointer-events-none opacity-50' : ''
                }`}
                onClick={() => {
                  dialog.destructAlert({
                    title: t('menu.menu-detail.product.edit.extra-action.delete.alert.title', 'Xác nhận xóa'),
                    message: t(
                      'menu.menu-detail.product.edit.extra-action.delete.alert.message',
                      'Bạn có chắc muốn xóa category này?',
                    ),
                    textButton: t('menu.menu-detail.product.edit.extra-action.delete.alert.text-button', 'Xóa'),
                    onConfirm: async () => {
                      await deleteMenuSection({ variables: { id: group.section?.id ?? '' } });
                      showAlert({
                        status: 'success',
                        title: t('common.success'),
                        message: t('Xóa category thành công!'),
                      });
                    },
                  });
                }}
              />
            )}
          </div>
        </div>
      </TableSection>
      {group.items
        .sort((a, b) => naturalCompare(a.code, b.code))
        .map((menuProductItem) => {
          const soldOutConditional = !group.section?.inStock || !menuProductItem.inStock;
          const basePrice = formatCurrency(
            menuProductItem.configurations
              .find((config) => config.type.includes('BASE'))
              ?.values.map((configValue) => getFragmentData(MenuProductConfigurationValueFragment, configValue))
              .at(0)?.price ?? 0,
            currency,
          );

          // Handler: Toggle enabled
          const handleToggleEnabled = () => {
            updateMenuProduct({
              variables: {
                id: menuProductItem.id,
                input: {
                  code: menuProductItem.code,
                  title: menuProductItem.title,
                  description: menuProductItem.description,
                  images: menuProductItem.images,
                  ingredients: menuProductItem.ingredients,
                  labels: menuProductItem.labels,
                  priceMode: menuProductItem.priceMode,
                  discountable: menuProductItem.discountable,
                  allergics: menuProductItem.allergics,
                  inStock: menuProductItem.inStock,
                  sectionId: menuProductItem.sectionId,
                  enabled: !menuProductItem.enabled,
                  configurations: processConfigurationData(
                    menuProductItem.configurations as MenuProductConfiguration[],
                    menuProductItem.priceMode,
                    menuProductItem.title,
                  ),
                },
              },
            })
              .then(() => {
                showAlert({
                  status: 'success',
                  title: t('common.success'),
                  message: t('Cập nhật trạng thái thành công!'),
                });
              })
              .catch(() => {
                showAlert({ status: 'error', title: t('common.error'), message: t('Cập nhật trạng thái thất bại!') });
              });
          };

          // Handler: Duplicate
          const handleDuplicate = () => {
            dialog.confirmAlert({
              title: t('menu.menu-detail.product.edit.extra-action.duplicate.confirm.title', 'Nhân bản sản phẩm'),
              message: t(
                'menu.menu-detail.product.edit.extra-action.duplicate.confirm.message',
                'Bạn có chắc muốn nhân bản sản phẩm này?',
              ),
              textButton: t('menu.menu-detail.product.edit.extra-action.duplicate.confirm.button', 'Nhân bản'),
              onConfirm: async () => {
                try {
                  const result = await duplicateMenuProduct({ variables: { id: menuProductItem.id } });
                  showAlert({
                    status: 'success',
                    title: t('common.success'),
                    message: t('Nhân bản sản phẩm thành công!'),
                  });

                  // Try to focus on the newly created product
                  const duplicatedProductId = result.data?.duplicateMenuProduct.id;
                  if (duplicatedProductId) {
                    // Wait for the DOM to update after refetch
                    setTimeout(() => {
                      const newProductElement = document.querySelector(`[data-product-id="${duplicatedProductId}"]`);
                      if (newProductElement) {
                        (newProductElement as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
                        (newProductElement as HTMLElement).focus();
                      }
                    }, 500);
                  }
                } catch (error) {
                  showAlert({
                    status: 'error',
                    title: t('common.error'),
                    message: t('Nhân bản sản phẩm thất bại!'),
                  });
                }
              },
            });
          };

          // Handler: Delete
          const handleDelete = () => {
            dialog.destructAlert({
              title: t('menu.menu-detail.product.edit.extra-action.delete.alert.title', 'Xác nhận xóa'),
              message: t(
                'menu.menu-detail.product.edit.extra-action.delete.alert.message',
                'Bạn có chắc muốn xóa sản phẩm này?',
              ),
              textButton: t('menu.menu-detail.product.edit.extra-action.delete.alert.text-button', 'Xóa'),
              onConfirm: async () => {
                await deleteMenuProduct({ variables: { id: menuProductItem.id } });
                showAlert({ status: 'success', title: t('common.success'), message: t('Xóa sản phẩm thành công!') });
              },
            });
          };

          return (
            <TableRow key={menuProductItem.id} data-product-id={menuProductItem.id}>
              {/* DotStatus column */}
              <TableRowColumn>
                <DotStatus enabled={menuProductItem.enabled} />
              </TableRowColumn>
              {/* Left: Info */}
              <TableRowColumn>
                <div className="flex flex-col gap-1 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{menuProductItem.code}</span>
                    <span className="text-gray-700 line-clamp-2">{menuProductItem.title}</span>
                    {soldOutConditional && (
                      <span className="hidden md:block border py-1 px-2 rounded-full border-red-600 bg-red-100 text-red-600 text-xs ml-2">
                        {t('menu-product.card.out-of-stock', 'Out of stock')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    {menuProductItem.configurations.some((config) => config.values.length > 1) && (
                      <span>{t('Start at')}</span>
                    )}
                    <span>{basePrice}</span>
                    {soldOutConditional && (
                      <span className="block md:hidden border py-1 px-2 rounded-full border-red-600 bg-red-100 text-red-600 text-xs">
                        {t('menu-product.card.out-of-stock', 'Out of stock')}
                      </span>
                    )}
                  </div>
                </div>
              </TableRowColumn>
              {/* Right: Quick Actions */}
              <TableRowColumn alignment={TableCellAlignment.Right}>
                <div className="flex items-center gap-2 justify-end">
                  <PencilIcon
                    onClick={() => {
                      navigate(`products/${menuProductItem.id}`);
                    }}
                    tabIndex={0}
                    aria-label="Edit"
                    role="button"
                    className="w-5 h-5 text-blue-500 cursor-pointer"
                  />
                  {/* Duplicate */}
                  <DocumentDuplicateIcon
                    tabIndex={0}
                    aria-label="Duplicate"
                    role="button"
                    className={`w-5 h-5 text-green-500 cursor-pointer ${
                      duplicating ? 'pointer-events-none opacity-50' : ''
                    }`}
                    onClick={handleDuplicate}
                  />
                  {/* Hide/Show on menu */}
                  {menuProductItem.enabled ? (
                    <EyeIcon
                      tabIndex={0}
                      aria-label="Visible"
                      role="button"
                      className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                        updating ? 'pointer-events-none opacity-50' : ''
                      }`}
                      onClick={handleToggleEnabled}
                    />
                  ) : (
                    <EyeSlashIcon
                      tabIndex={0}
                      aria-label="Hidden"
                      role="button"
                      className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                        updating ? 'pointer-events-none opacity-50' : ''
                      }`}
                      onClick={handleToggleEnabled}
                    />
                  )}
                  {/* Delete */}
                  <TrashIcon
                    tabIndex={0}
                    aria-label="Delete"
                    role="button"
                    className={`w-5 h-5 text-red-500 cursor-pointer ${
                      deleting ? 'pointer-events-none opacity-50' : ''
                    }`}
                    onClick={handleDelete}
                  />
                </div>
              </TableRowColumn>
            </TableRow>
          );
        })}
    </>
  );
}
